import { useEffect, useState, useCallback, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import DonationModal from '@/components/DonationModal'
import InstagramEmbed from '@/components/InstagramEmbed'
import type { SiteSetting, TeamMember } from '@/types'
import {Heart, Users, Clock, Award, Eye, Gem, Instagram, Phone, Mail, MapPin, Utensils, Dumbbell, Trees, Home as HomeIcon, Stethoscope, BedDouble, HandHeart, Apple, Pill, Handshake, Quote, MessageSquare, ChevronLeft, ChevronRight, X, ArrowRight, ChevronDown, UserCircle2, } from 'lucide-react'

/* ── Paleta institucional — inspirada SISMEDI (azul) ─── */
const TEAL       = '#1A56DB'        // azul principal
const TEAL_LIGHT = '#3B72F0'        // azul hover/claro
const TEAL_BG    = '#EFF6FF'        // azul ultra-leve para fundos
const DARK       = '#1E3A5F'        // azul-marinho escuro
const NAVY       = '#1E3A5F'        // azul-marinho profundo
const GOLD       = '#E8710A'        // laranja vibrante (CTA)
const PAGE_BG    = '#F8FBFF'        // fundo geral: quase branco com toque azul

const STRUCTURE_ICONS = [BedDouble, Trees, Dumbbell, Users, Utensils, Stethoscope]
const DEFAULT_STRUCT_LABELS = [
  'Quartos Climatizados', 'Jardim Terapêutico', 'Fisioterapia',
  'Área de Convivência', 'Refeitório', 'Enfermaria 24h',
]

interface Partner    { _id: string; name: string; logoUrl?: string; website?: string; isActive?: boolean; order?: number }
interface Testimonial { _id: string; authorName: string; authorRole?: string; text: string; status: string; photoUrl?: string }

/* ── Componente Hero Carousel ─────────────────────────── */
function HeroCarousel({
  images, title, subtitle, onDonate, onAbout,
}: {
  images: string[]; title: string; subtitle: string; onDonate: () => void; onAbout: () => void
}) {
  const [current, setCurrent] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const total = images.length

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    if (total > 1) {
      timerRef.current = setInterval(() => {
        setCurrent(p => (p + 1) % total)
      }, 7000)
    }
  }, [total])

  useEffect(() => {
    startTimer()
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [startTimer])

  const go = (idx: number) => {
    setCurrent(idx)
    startTimer()
  }

  return (
    <section className="w-full">
      {/* ── Imagem(ns) full-width ── */}
      <div className="relative w-full overflow-hidden bg-gray-200" style={{ aspectRatio: '1440/560' }}>
        {images.length > 0 ? (
          images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`Banner ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: current === i ? 1 : 0, zIndex: current === i ? 1 : 0 }}
            />
          ))
        ) : (
          /* Fallback sem imagem */
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: TEAL_BG }}>
            <HomeIcon size={64} style={{ color: TEAL_LIGHT }} />
          </div>
        )}

        {/* Controles do carrossel */}
        {total > 1 && (
          <>
            {/* Setas */}
            <button
              onClick={() => go((current - 1 + total) % total)}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow transition-all"
              aria-label="Banner anterior"
            >
              <ChevronLeft size={18} className="text-gray-700" />
            </button>
            <button
              onClick={() => go((current + 1) % total)}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow transition-all"
              aria-label="Próximo banner"
            >
              <ChevronRight size={18} className="text-gray-700" />
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  className="rounded-full transition-all"
                  style={{
                    width: current === i ? 20 : 8,
                    height: 8,
                    background: current === i ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}
                  aria-label={`Ir para banner ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Faixa de texto abaixo da imagem ── */}
      <div className="w-full bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-8 sm:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex-1 min-w-0">
            <div className="w-10 h-1 rounded-full mb-3" style={{ background: TEAL }} />
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-black leading-snug text-gray-900 mb-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {title}
            </h1>
            <p className="text-sm sm:text-base text-gray-500 leading-relaxed max-w-xl">
              {subtitle}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            <button
              onClick={onAbout}
              className="px-5 py-2.5 rounded-xl text-sm font-bold border-2 text-gray-700 transition-all"
              style={{ borderColor: '#bfdbfe' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL; e.currentTarget.style.color = TEAL }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.color = '' }}
            >
              Conheça nossa história
            </button>
            <button
              onClick={onDonate}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all shadow-sm"
              style={{ background: GOLD }}
              onMouseEnter={e => (e.currentTarget.style.background = '#CF6008')}
              onMouseLeave={e => (e.currentTarget.style.background = GOLD)}
            >
              <Heart size={14} />
              Fazer doação
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── Componente principal ─────────────────────────────── */
export default function Home() {
  const [settings, setSettings]           = useState<Record<string, string>>({})
  const [partners, setPartners]           = useState<Partner[]>([])
  const [testimonials, setTestimonials]   = useState<Testimonial[]>([])
  const [teamMembers, setTeamMembers]     = useState<TeamMember[]>([])
  const [loading, setLoading]             = useState(true)
  const [donationOpen, setDonationOpen]   = useState(false)
  const [testimonialIdx, setTestimonialIdx] = useState(0)
  const [showTestimonialForm, setShowTestimonialForm] = useState(false)
  const [newTestimonial, setNewTestimonial] = useState({ authorName: '', authorRole: '', text: '' })
  const [testimonialPhoto, setTestimonialPhoto] = useState<File | null>(null)
  const [testimonialPhotoPreview, setTestimonialPhotoPreview] = useState('')
  const [submittingTestimonial, setSubmittingTestimonial] = useState(false)
  const [contactForm, setContactForm]     = useState({ name: '', email: '', phone: '', message: '' })
  const [submittingContact, setSubmittingContact] = useState(false)
  const [contactSent, setContactSent]     = useState(false)
  const [partnerIdx, setPartnerIdx]       = useState(0)

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, partnersRes, testimonialsRes, teamRes] = await Promise.all([
        lumi.entities.site_settings.list({ limit: 150 }),
        lumi.entities.partners.list({ filter: { isActive: true }, sort: { order: 1 }, limit: 50 }),
        lumi.entities.testimonials.list({ filter: { status: 'approved' }, sort: { createdAt: -1 }, limit: 12 }),
        lumi.entities.team_members.list({ filter: { isActive: true }, sort: { order: 1 }, limit: 100 }),
      ])
      const map: Record<string, string> = {}
      ;(settingsRes.list as SiteSetting[]).forEach(s => { map[s.key] = s.value })
      setSettings(map)
      setPartners(partnersRes.list as Partner[])
      setTestimonials(testimonialsRes.list as Testimonial[])
      setTeamMembers(teamRes.list as TeamMember[])
      document.title = map['site_title'] || '(PALACIO DA SABEDORIA'
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', map['meta_description'] || 'Palácio da Sabedoria — acolhimento de idosos com amor, respeito e dignidade.')
    } catch (e) {
      console.error('Erro ao carregar dados:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const s = (key: string, fallback = '') => settings[key] || fallback

  /* Hero — até 4 imagens */
  const heroImages = [
    s('header_image'), s('hero_image_2'), s('hero_image_3'), s('hero_image_4'),
  ].filter(Boolean)

  const heroTitle    = s('hero_title', 'Um lar de amor, respeito e dignidade para quem já cuidou de todos nós.')
  const heroSubtitle = s('hero_subtitle', 'Há mais de 15 anos oferecendo acolhimento, segurança, saúde e carinho para nossos residentes.')
  const siteName     = s('site_title', '(Remix) palacinho')

  const stat1Value = s('stat_residents', '87')
  const stat2Value = s('stat_professionals', '24')
  const stat3Label = s('stat_3_label', 'ATENDIMENTO')
  const stat3Value = s('stat_3_value', '24 HORAS')
  const stat4Label = s('stat_4_label', 'MAIS DE 15 ANOS')
  const stat4Suffix = s('stat_4_suffix', 'DE ATUAÇÃO')

  const aboutImage   = s('about_image')
  const aboutTitle   = s('about_title', 'Sobre o Palácio da Sabedoria')
  const missionText  = s('mission_text', 'Promover qualidade de vida, dignidade e acolhimento para pessoas idosas através de um atendimento humanizado.')
  const visionText   = s('vision_text', 'Ser referência regional em cuidado e assistência à pessoa idosa, com excelência, inovação e amor.')
  const valuesText   = s('values_text', 'Respeito\nAmor\nTransparência\nHumanização\nÉtica')

  const instagramUrl       = s('instagram_url', '#')
  const instagramHandle    = s('instagram_handle', '@palaciodosidosos')
  const instagramEmbedCode = s('instagram_embed_code', '')
  const pixKey             = s('pix_key', '')
  const address            = s('footer_address', '')
  const phone              = s('footer_phone', '')
  const email              = s('footer_email', '')
  const contactTitle       = s('contact_title', 'Entre em contato')

  const whatsappSupporter = s('whatsapp_supporter', '')
  const whatsappLink = whatsappSupporter
    ? `https://wa.me/${whatsappSupporter.replace(/\D/g, '')}?text=${encodeURIComponent('Olá! Quero ser Apoiador mensal e vim pelo site da instituição.')}`
    : ''

  const handleTestimonialPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setTestimonialPhoto(file)
    const reader = new FileReader()
    reader.onload = ev => setTestimonialPhotoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleSubmitTestimonial = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTestimonial.authorName.trim() || !newTestimonial.text.trim()) return
    setSubmittingTestimonial(true)
    try {
      const now = new Date().toISOString()
      let photoUrl = ''
      if (testimonialPhoto) {
        try {
          const res = await lumi.tools.file.upload([testimonialPhoto])
          photoUrl = res?.[0]?.url || ''
        } catch { /* foto é opcional */ }
      }
      await lumi.entities.testimonials.create({
        authorName: newTestimonial.authorName.trim(),
        authorRole: newTestimonial.authorRole.trim(),
        text: newTestimonial.text.trim(),
        ...(photoUrl ? { photoUrl } : {}),
        status: 'pending',
        creator: 'public',
        createdAt: now,
        updatedAt: now,
      })
      setNewTestimonial({ authorName: '', authorRole: '', text: '' })
      setTestimonialPhoto(null)
      setTestimonialPhotoPreview('')
      setShowTestimonialForm(false)
      alert('Depoimento enviado! Ele passará por análise antes de ser publicado. Obrigado!')
    } catch {
      alert('Erro ao enviar depoimento. Tente novamente.')
    } finally {
      setSubmittingTestimonial(false)
    }
  }

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) return
    setSubmittingContact(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.contact_messages.create({
        name: contactForm.name.trim(),
        email: contactForm.email.trim(),
        phone: contactForm.phone.trim(),
        message: contactForm.message.trim(),
        status: 'unread',
        creator: 'public',
        createdAt: now,
        updatedAt: now,
      })
      setContactForm({ name: '', email: '', phone: '', message: '' })
      setContactSent(true)
    } catch {
      alert('Erro ao enviar mensagem. Tente novamente.')
    } finally {
      setSubmittingContact(false)
    }
  }

  const PARTNERS_PER_PAGE = 4
  const totalPartnerPages = Math.ceil(partners.length / PARTNERS_PER_PAGE)
  const currentPartners   = partners.slice(partnerIdx * PARTNERS_PER_PAGE, (partnerIdx + 1) * PARTNERS_PER_PAGE)
  const TESTI_PER_PAGE    = 3
  const totalTestiPages   = Math.ceil(testimonials.length / TESTI_PER_PAGE)
  const currentTestis     = testimonials.slice(testimonialIdx * TESTI_PER_PAGE, (testimonialIdx + 1) * TESTI_PER_PAGE)

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: TEAL_BG, borderTopColor: TEAL }} />
      </div>
    )
  }

  /* ── Render ────────────────────────────────────────── */
  return (
    <div className="min-h-screen transition-colors" style={{ background: PAGE_BG }}>
      <Header />

      {/* ════════════════════════════════════════
          HERO CAROUSEL
      ════════════════════════════════════════ */}
      <HeroCarousel
        images={heroImages}
        title={heroTitle}
        subtitle={heroSubtitle}
        onDonate={() => setDonationOpen(true)}
        onAbout={() => scrollTo('sobre')}
      />

      {/* ════════════════════════════════════════
          ESTATÍSTICAS
      ════════════════════════════════════════ */}
      <section id="stats" className="w-full bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-gray-100">
          {[
            { icon: Users,     value: stat1Value, label: 'Residentes' },
            { icon: Handshake, value: stat2Value, label: 'Profissionais' },
            { icon: Clock,     value: stat3Value, label: stat3Label },
            { icon: Heart,     value: stat4Label, label: stat4Suffix },
          ].map(({ icon: Icon, value, label }) => (
            <div key={label} className="flex items-center gap-3 px-5 py-3 first:pl-0 last:pr-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: TEAL_BG }}>
                <Icon size={20} style={{ color: TEAL }} />
              </div>
              <div>
                <div className="text-lg sm:text-xl font-black text-gray-900 leading-tight">{value}</div>
                <div className="text-xs text-gray-400 font-medium tracking-wide">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          SOBRE
      ════════════════════════════════════════ */}
      <section id="sobre" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Foto */}
            <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-gray-50" style={{ minHeight: 340 }}>
              {aboutImage ? (
                <img src={aboutImage} alt="Palácio da Sabedoria" className="w-full h-full object-cover" style={{ minHeight: 340 }} />
              ) : (
                <div className="w-full flex items-center justify-center" style={{ minHeight: 340, background: TEAL_BG }}>
                  <HomeIcon size={56} style={{ color: TEAL_LIGHT }} />
                </div>
              )}
            </div>
            {/* Conteúdo */}
            <div className="pt-2">
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: TEAL }} />
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {aboutTitle}
              </h2>
              <div className="space-y-7">
                {[
                  { icon: Heart, color: TEAL,  title: 'Nossa Missão', text: missionText },
                  { icon: Eye,   color: NAVY,  title: 'Nossa Visão',  text: visionText },
                ].map(({ icon: Icon, color, title, text }) => (
                  <div key={title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: color + '12' }}>
                      <Icon size={20} style={{ color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm mb-1">{title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{text}</p>
                    </div>
                  </div>
                ))}
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: GOLD + '15' }}>
                    <Gem size={20} style={{ color: GOLD }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-2">Nossos Valores</h3>
                    <div className="flex flex-wrap gap-2">
                      {valuesText.split('\n').filter(Boolean).map(v => (
                        <span key={v} className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: TEAL_BG, color: '#1447C0' }}>
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          ESTRUTURA
      ════════════════════════════════════════ */}
      <section id="estrutura" className="py-16" style={{ background: TEAL_BG }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-10">
            <div className="w-8 h-1 rounded-full mx-auto mb-4" style={{ background: TEAL }} />
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Nossa Estrutura
            </h2>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Ambientes pensados para o conforto, segurança e bem-estar dos nossos residentes.
            </p>
          </div>
          {(() => {
            const structItems = Array.from({ length: 6 }, (_, i) => {
              const Icon  = STRUCTURE_ICONS[i]
              const img   = s(`struct_img_${i + 1}`)
              const label = s(`struct_label_${i + 1}`, DEFAULT_STRUCT_LABELS[i])
              return { Icon, img, label, key: `struct_img_${i + 1}` }
            }).filter(item => item.img)

            if (structItems.length === 0) return (
              <p className="text-center text-gray-400 text-sm py-8">
                Nenhuma foto da estrutura cadastrada ainda. Adicione pelo painel administrativo.
              </p>
            )
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {structItems.map(({ Icon, img, label, key }) => (
                  <div
                    key={key}
                    className="rounded-2xl overflow-hidden relative group shadow-sm"
                    style={{ aspectRatio: '1/1', background: '#c4d9f8' }}
                  >
                    <img src={img} alt={label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm px-2 py-1.5 flex items-center gap-1.5">
                      <Icon size={11} style={{ color: TEAL, flexShrink: 0 }} />
                      <span className="text-xs font-semibold text-gray-700 truncate">{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          })()}
          <div className="text-center mt-8">
            <button
              onClick={() => scrollTo('contato')}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all border-2"
              style={{ borderColor: TEAL, color: TEAL }}
              onMouseEnter={e => { e.currentTarget.style.background = TEAL; e.currentTarget.style.color = '#fff' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEAL }}
            >
              Conheça toda a estrutura
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          DOAÇÃO
      ════════════════════════════════════════ */}
      <section id="doacao" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-12">
            <div className="w-8 h-1 rounded-full mx-auto mb-4" style={{ background: GOLD }} />
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Ajude a transformar vidas
            </h2>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Sua generosidade faz toda a diferença no dia a dia dos nossos residentes.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Esquerda — benefícios */}
            <div className="space-y-4">
              {[
                { icon: HandHeart,  label: 'Acolhimento com amor',     color: TEAL },
                { icon: Heart,      label: 'Cuidado diário',           color: '#e27272' },
                { icon: Stethoscope,label: 'Saúde e bem-estar',        color: NAVY },
                { icon: Gem,        label: 'Dignidade sempre',         color: GOLD },
              ].map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + '15' }}>
                    <Icon size={18} style={{ color }} />
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{label}</span>
                </div>
              ))}
            </div>

            {/* Centro — PIX */}
            <div className="flex flex-col items-center text-center">
              <div
                className="w-full rounded-2xl p-6 border mb-4"
                style={{ background: TEAL_BG, borderColor: TEAL_LIGHT }}
              >
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: TEAL }}>
                  Doação via PIX
                </p>
                <div className="flex items-center justify-center mb-4">
                  <span className="text-5xl">📱</span>
                </div>
                {pixKey && (
                  <div className="px-3 py-2 rounded-lg bg-white border text-xs font-mono text-gray-600 mb-4 truncate" style={{ borderColor: TEAL_LIGHT }}>
                    {pixKey}
                  </div>
                )}
                <button
                  onClick={() => setDonationOpen(true)}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all shadow-sm"
                  style={{ background: TEAL }}
                  onMouseEnter={e => (e.currentTarget.style.background = TEAL_LIGHT)}
                  onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
                >
                  <Heart size={14} className="inline mr-1.5" />
                  Gerar QR Code e Doar
                </button>
              </div>
            </div>

            {/* Direita — apoiador mensal */}
            <div className="space-y-4">
              <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50">
                <h3 className="text-sm font-bold text-gray-800 mb-1">Apoiador Mensal</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">
                  Torne-se um apoiador mensal e garanta suporte contínuo aos nossos residentes.
                </p>
                {whatsappLink ? (
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                    style={{ background: '#25D366' }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                  >
                    <Users size={14} />
                    Tornar-se Apoiador Mensal
                  </a>
                ) : (
                  <button
                    onClick={() => scrollTo('contato')}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold transition-all border-2"
                    style={{ borderColor: NAVY, color: NAVY }}
                    onMouseEnter={e => { e.currentTarget.style.background = NAVY; e.currentTarget.style.color = '#fff' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = NAVY }}
                  >
                    <Users size={14} />
                    Tornar-se Apoiador Mensal
                  </button>
                )}
              </div>
              <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50">
                <h3 className="text-sm font-bold text-gray-800 mb-1">Doação de Itens</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-3">
                  Alimentos, medicamentos e materiais de higiene também são muito bem-vindos.
                </p>
                <button
                  onClick={() => scrollTo('contato')}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold transition-all border-2"
                  style={{ borderColor: TEAL, color: TEAL }}
                  onMouseEnter={e => { e.currentTarget.style.background = TEAL; e.currentTarget.style.color = '#fff' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEAL }}
                >
                  Entrar em contato
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          INSTAGRAM
      ════════════════════════════════════════ */}
      <section id="instagram" className="py-16" style={{ background: PAGE_BG }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: '#E1306C' }} />
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Acompanhe nosso dia a dia
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Siga nosso Instagram e veja como é a vida aqui no {siteName}.
              </p>
            </div>
            {instagramUrl !== '#' && (
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border transition-all flex-shrink-0"
                style={{ borderColor: '#d1d5db', color: '#6b7280' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#E1306C'; e.currentTarget.style.color = '#E1306C' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280' }}
              >
                <Instagram size={15} />
                {instagramHandle || 'Ver Instagram'}
              </a>
            )}
          </div>

          {instagramEmbedCode ? (
            <InstagramEmbed html={instagramEmbedCode} />
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
              <Instagram size={36} className="mx-auto mb-3" style={{ color: TEAL_LIGHT }} />
              <p className="text-sm text-gray-400">
                Configure a incorporação Behold no painel administrativo para exibir o feed do Instagram.
              </p>
              {instagramUrl !== '#' && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold"
                  style={{ color: TEAL }}
                >
                  <Instagram size={14} />
                  {instagramHandle || 'Visitar Instagram'}
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════
          COMO AJUDAR
      ════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-10">
            <div className="w-8 h-1 rounded-full mx-auto mb-4" style={{ background: TEAL }} />
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {s('how_to_help_title', 'Como você pode ajudar')}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {[
              { icon: Heart,     color: GOLD,     label: s('help_1_label', 'Doação Financeira'),    desc: s('help_1_desc', 'Toda contribuição faz a diferença!') },
              { icon: Apple,     color: '#e27272', label: s('help_2_label', 'Doação de Alimentos'),  desc: s('help_2_desc', 'Alimentos não perecíveis são sempre bem-vindos.') },
              { icon: Pill,      color: TEAL,     label: s('help_3_label', 'Medicamentos'),          desc: s('help_3_desc', 'Dentro do prazo de validade.') },
              { icon: Handshake, color: NAVY,     label: s('help_4_label', 'Trabalho Voluntário'),   desc: s('help_4_desc', 'Seu tempo e carinho transformam vidas.') },
            ].map(({ icon: Icon, color, label, desc }) => (
              <div key={label} className="rounded-2xl border border-gray-100 bg-gray-50 p-5 text-center hover:shadow-sm transition-shadow">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center" style={{ background: color + '15' }}>
                  <Icon size={24} style={{ color }} />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1.5">{label}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          NOSSA EQUIPE
      ════════════════════════════════════════ */}
      <section id="equipe" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center mb-12">
            <div className="w-8 h-1 rounded-full mx-auto mb-4" style={{ background: TEAL }} />
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Nossa Equipe
            </h2>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Profissionais dedicados que cuidam com amor e competência dos nossos residentes.
            </p>
          </div>

          {teamMembers.length === 0 ? (
            <div className="text-center py-14 rounded-2xl border border-dashed border-gray-100" style={{ background: TEAL_BG }}>
              <UserCircle2 size={40} className="mx-auto mb-3" style={{ color: TEAL_LIGHT }} />
              <p className="text-gray-400 text-sm">Colaboradores serão apresentados aqui em breve.</p>
            </div>
          ) : (
            <div
              className="grid gap-5"
              style={{
                gridTemplateColumns: teamMembers.length <= 4
                  ? `repeat(${teamMembers.length}, minmax(0, 1fr))`
                  : teamMembers.length <= 8
                  ? 'repeat(4, minmax(0, 1fr))'
                  : 'repeat(4, minmax(0, 1fr))',
              }}
            >
              {teamMembers.map((member, idx) => (
                <div
                  key={member._id}
                  className="group flex flex-col items-center text-center p-5 rounded-2xl border border-gray-100 bg-gray-50 hover:shadow-lg hover:-translate-y-1 hover:border-[#bfdbfe] transition-all duration-300"
                  style={{
                    animationDelay: `${idx * 60}ms`,
                  }}
                >
                  {/* Foto / Avatar */}
                  <div
                    className="w-20 h-20 rounded-full overflow-hidden mb-4 flex items-center justify-center flex-shrink-0 ring-4 ring-white shadow-sm transition-all duration-300 group-hover:ring-[#1A56DB] group-hover:shadow-lg"
                    style={{ background: TEAL_BG }}
                  >
                    {member.photoUrl ? (
                      <img
                        src={member.photoUrl}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-125"
                      />
                    ) : (
                      <span
                        className="text-2xl font-black transition-transform duration-300 group-hover:scale-125 inline-block"
                        style={{ color: TEAL }}
                      >
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Linha decorativa */}
                  <div className="w-8 h-0.5 rounded-full mb-3" style={{ background: TEAL_LIGHT }} />

                  {/* Nome */}
                  <h3 className="font-bold text-gray-900 text-sm leading-snug mb-1">
                    {member.name}
                  </h3>

                  {/* Função */}
                  <span
                    className="text-xs font-semibold px-3 py-1 rounded-full"
                    style={{ background: TEAL_BG, color: '#1447C0' }}
                  >
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════
          DEPOIMENTOS
      ════════════════════════════════════════ */}
      <section className="py-16" style={{ background: TEAL_BG }}>
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: TEAL }} />
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {s('testimonials_title', 'O que dizem sobre nós')}
              </h2>
            </div>
            <button
              onClick={() => setShowTestimonialForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white flex-shrink-0 transition-all shadow-sm"
              style={{ background: TEAL }}
              onMouseEnter={e => (e.currentTarget.style.background = TEAL_LIGHT)}
              onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
            >
              <MessageSquare size={15} />
              Deixar Depoimento
            </button>
          </div>

          {testimonials.length === 0 ? (
            <div className="text-center py-14 rounded-2xl bg-white border border-gray-100">
              <Quote size={36} className="mx-auto mb-3" style={{ color: TEAL_LIGHT }} />
              <p className="text-gray-400 text-sm">Nenhum depoimento publicado ainda. Seja o primeiro a deixar o seu!</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-3 gap-5">
                {currentTestis.map((t, i) => (
                  <div
                    key={(t as any)._id || i}
                    className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex flex-col"
                  >
                    <Quote size={24} style={{ color: TEAL_LIGHT }} className="mb-3 flex-shrink-0" />
                    <p className="text-sm text-gray-600 leading-relaxed italic mb-5 flex-1">{t.text}</p>
                    <div className="flex items-center gap-3 pt-3 border-t border-gray-50">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" style={{ background: TEAL }}>
                        {(t as any).photoUrl ? (
                          <img src={(t as any).photoUrl} alt={t.authorName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                            {t.authorName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-sm">{t.authorName}</div>
                        {t.authorRole && <div className="text-xs text-gray-400">{t.authorRole}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalTestiPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-7">
                  <button
                    onClick={() => setTestimonialIdx(p => Math.max(0, p - 1))}
                    disabled={testimonialIdx === 0}
                    className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-30 transition-colors shadow-sm"
                    style={{} as React.CSSProperties}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = TEAL)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '')}
                  >
                    <ChevronLeft size={15} className="text-gray-500" />
                  </button>
                  {Array.from({ length: totalTestiPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setTestimonialIdx(i)}
                      className="rounded-full transition-all"
                      style={{ width: testimonialIdx === i ? 20 : 8, height: 8, background: testimonialIdx === i ? TEAL : '#cbd5e1' }}
                    />
                  ))}
                  <button
                    onClick={() => setTestimonialIdx(p => Math.min(totalTestiPages - 1, p + 1))}
                    disabled={testimonialIdx === totalTestiPages - 1}
                    className="p-2 rounded-full bg-white border border-gray-200 disabled:opacity-30 transition-colors shadow-sm"
                    onMouseEnter={e => (e.currentTarget.style.borderColor = TEAL)}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '')}
                  >
                    <ChevronRight size={15} className="text-gray-500" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal depoimento */}
        {showTestimonialForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-up">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Deixe seu Depoimento</h3>
                <button onClick={() => setShowTestimonialForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">Seu depoimento passará por análise antes de ser publicado.</p>
              <form onSubmit={handleSubmitTestimonial} className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer flex-shrink-0">
                    <div
                      className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center border-2 border-dashed"
                      style={{ borderColor: TEAL, background: testimonialPhotoPreview ? 'transparent' : TEAL_BG }}
                    >
                      {testimonialPhotoPreview ? (
                        <img src={testimonialPhotoPreview} alt="preview" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold" style={{ color: TEAL }}>FOTO</span>
                      )}
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={handleTestimonialPhotoChange} />
                  </label>
                  <p className="text-xs text-gray-400">Adicione uma foto <span className="text-gray-300">(opcional)</span></p>
                </div>
                <input
                  type="text" placeholder="Seu nome *" value={newTestimonial.authorName} required
                  onChange={e => setNewTestimonial(prev => ({ ...prev, authorName: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                <input
                  type="text" placeholder="Sua relação com a instituição (ex: Familiar, Voluntário)"
                  value={newTestimonial.authorRole}
                  onChange={e => setNewTestimonial(prev => ({ ...prev, authorRole: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                <textarea
                  placeholder="Seu depoimento *" value={newTestimonial.text} required rows={4}
                  onChange={e => setNewTestimonial(prev => ({ ...prev, text: e.target.value }))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none resize-none"
                  onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}40`)}
                  onBlur={e => (e.currentTarget.style.boxShadow = '')}
                />
                <div className="flex gap-2 pt-1">
                  <button
                    type="button" onClick={() => setShowTestimonialForm(false)}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit" disabled={submittingTestimonial}
                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                    style={{ background: TEAL }}
                  >
                    {submittingTestimonial ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* ════════════════════════════════════════
          PARCEIROS
      ════════════════════════════════════════ */}
      {partners.length > 0 && (
        <section className="py-12 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
            <p className="text-xs font-bold uppercase tracking-widest text-center mb-8" style={{ color: '#9ca3af' }}>
              {s('partners_title', 'Nossos Parceiros')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {currentPartners.map(partner => (
                <div
                  key={partner._id}
                  className="flex flex-col items-center justify-center p-4 rounded-2xl border border-gray-100 bg-gray-50 hover:shadow-sm transition-shadow min-h-[72px]"
                >
                  {partner.website ? (
                    <a href={partner.website} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5">
                      {partner.logoUrl ? (
                        <img src={partner.logoUrl} alt={partner.name} className="h-9 w-auto object-contain" />
                      ) : (
                        <>
                          <Award size={20} style={{ color: TEAL }} />
                          <span className="text-xs font-semibold text-gray-500 text-center leading-tight">{partner.name}</span>
                        </>
                      )}
                    </a>
                  ) : (
                    <div className="flex flex-col items-center gap-1.5">
                      {partner.logoUrl ? (
                        <img src={partner.logoUrl} alt={partner.name} className="h-9 w-auto object-contain" />
                      ) : (
                        <>
                          <Award size={20} style={{ color: TEAL }} />
                          <span className="text-xs font-semibold text-gray-500 text-center leading-tight">{partner.name}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {totalPartnerPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <button
                  onClick={() => setPartnerIdx(p => Math.max(0, p - 1))}
                  disabled={partnerIdx === 0}
                  className="p-2 rounded-full border border-gray-200 disabled:opacity-30 transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.borderColor = TEAL)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '')}
                >
                  <ChevronLeft size={15} className="text-gray-500" />
                </button>
                {Array.from({ length: totalPartnerPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPartnerIdx(i)}
                    className="rounded-full transition-all"
                    style={{ width: partnerIdx === i ? 20 : 8, height: 8, background: partnerIdx === i ? TEAL : '#d1d5db' }}
                  />
                ))}
                <button
                  onClick={() => setPartnerIdx(p => Math.min(totalPartnerPages - 1, p + 1))}
                  disabled={partnerIdx === totalPartnerPages - 1}
                  className="p-2 rounded-full border border-gray-200 disabled:opacity-30 transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.borderColor = TEAL)}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '')}
                >
                  <ChevronRight size={15} className="text-gray-500" />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ════════════════════════════════════════
          CONTATO
      ════════════════════════════════════════ */}
      <section id="contato" className="py-20" style={{ background: PAGE_BG }}>
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Formulário */}
            <div>
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: TEAL }} />
              <h2 className="text-2xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {contactTitle}
              </h2>
              {contactSent ? (
                <div className="flex flex-col items-center justify-center py-12 text-center rounded-2xl bg-white border border-gray-100">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: TEAL_BG }}>
                    <Mail size={24} style={{ color: TEAL }} />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">Mensagem enviada!</h3>
                  <p className="text-sm text-gray-400 mb-5">Entraremos em contato em breve.</p>
                  <button
                    onClick={() => setContactSent(false)}
                    className="px-5 py-2 rounded-xl text-sm font-bold border-2 transition-all"
                    style={{ borderColor: TEAL, color: TEAL }}
                  >
                    Enviar outra mensagem
                  </button>
                </div>
              ) : (
                <form className="space-y-3 bg-white rounded-2xl border border-gray-100 p-6" onSubmit={handleSubmitContact}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      type="text" placeholder="Nome completo *" required
                      value={contactForm.name}
                      onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:bg-white transition-colors"
                      onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}30`)}
                      onBlur={e => (e.currentTarget.style.boxShadow = '')}
                    />
                    <input
                      type="email" placeholder="E-mail *" required
                      value={contactForm.email}
                      onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:bg-white transition-colors"
                      onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}30`)}
                      onBlur={e => (e.currentTarget.style.boxShadow = '')}
                    />
                  </div>
                  <input
                    type="tel" placeholder="Telefone"
                    value={contactForm.phone}
                    onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:bg-white transition-colors"
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}30`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                  <textarea
                    placeholder="Mensagem *" required rows={4}
                    value={contactForm.message}
                    onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 focus:outline-none focus:bg-white transition-colors resize-none"
                    onFocus={e => (e.currentTarget.style.boxShadow = `0 0 0 2px ${TEAL}30`)}
                    onBlur={e => (e.currentTarget.style.boxShadow = '')}
                  />
                  <button
                    type="submit" disabled={submittingContact}
                    className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60"
                    style={{ background: TEAL }}
                    onMouseEnter={e => (e.currentTarget.style.background = TEAL_LIGHT)}
                    onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
                  >
                    {submittingContact ? 'Enviando...' : 'Enviar mensagem'}
                  </button>
                </form>
              )}
            </div>

            {/* Informações */}
            <div className="pt-2">
              <div className="w-8 h-1 rounded-full mb-4" style={{ background: TEAL }} />
              <h2 className="text-2xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Informações
              </h2>
              <div className="space-y-4">
                {address && (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-gray-100">
                    <MapPin size={18} style={{ color: TEAL }} className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{address}</p>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100">
                    <Phone size={18} style={{ color: TEAL }} className="flex-shrink-0" />
                    <a href={`tel:${phone}`} className="text-sm text-gray-600 hover:underline">{phone}</a>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-gray-100">
                    <Mail size={18} style={{ color: TEAL }} className="flex-shrink-0" />
                    <a href={`mailto:${email}`} className="text-sm text-gray-600 hover:underline">{email}</a>
                  </div>
                )}
                {!address && !phone && !email && (
                  <div className="p-4 rounded-2xl bg-white border border-gray-100">
                    <p className="text-sm text-gray-400">Configure as informações de contato no painel administrativo.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <DonationModal
        isOpen={donationOpen}
        onClose={() => setDonationOpen(false)}
        pixKey={pixKey || 'contato@palaciodosidosos.org.br'}
      />
    </div>
  )
}
